import {
  useKineticMap,
  useStableResultData,
  useGetNodes,
  MapContext,
  DEFAULT_ZOOM,
  useMapBoxSearchContext,
  MapSearchResultItem,
  useMapSearchBoxPin,
  MapSearchResultProps,
  AppSearchIconKey,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
} from '@kinetic-ui/shared';
import { useTravelTimeListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { Timer as TimerIcon } from '@mui/icons-material';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../consts';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();

  const result = useTravelTimeListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const travelTimes = useGetNodes(resultData?.travelTimes);

  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const travelTime = travelTimes.find((e) => e.id === id);
        if (travelTime?.name) {
          addSearchHistory({
            name: travelTime.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.TravelTime,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }

      !selectedLayers.includes('travelTimes') &&
        setSelectedLayers([...selectedLayers, 'travelTimes']);

      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);

      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
      travelTimes,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {travelTimes.map((travelTime) => (
        <MapSearchResultItem
          key={`searchItem_${travelTime.id}`}
          name={travelTime.name}
          onClick={(openInNewWindow) =>
            onClick(travelTime.id, travelTime.geometry, openInNewWindow)
          }
          icon={<TimerIcon />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
